import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { ArrowRight, MessageSquarePlus, Search, Sparkles, X } from "lucide-react";
import { useSearchIndex } from "./SearchIndexContext";
import { searchHits } from "./SearchIndex";
import { SearchResults } from "./SearchPanel";
import { EnquiryDialog } from "./EnquiryDialog";

type HeroSearchProps = {
  size?: "default" | "large";
  variant?: "solid" | "glass";
  rotatingPlaceholders?: string[];
  suggestions?: string[];
  autoFocus?: boolean;
  resultsPosition?: "below" | "above";
};

export function HeroSearch({
  size = "default",
  variant = "solid",
  rotatingPlaceholders,
  suggestions,
  autoFocus = false,
  resultsPosition = "below",
}: HeroSearchProps = {}) {
  const [query, setQuery] = useState("");
  const [activeIdx, setActiveIdx] = useState(0);
  const [placeholderIdx, setPlaceholderIdx] = useState(0);
  const [placeholderVisible, setPlaceholderVisible] = useState(true);
  const [enquiryOpen, setEnquiryOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const index = useSearchIndex();
  const results = useMemo(() => searchHits(index, query), [index, query]);

  const isLarge = size === "large";
  const placeholders = rotatingPlaceholders && rotatingPlaceholders.length > 0
    ? rotatingPlaceholders
    : ["Search services, insights, people…"];

  useEffect(() => setActiveIdx(0), [query]);

  useEffect(() => {
    if (placeholders.length <= 1) return;
    const interval = setInterval(() => {
      setPlaceholderVisible(false);
      const timeout = setTimeout(() => {
        setPlaceholderIdx((i) => (i + 1) % placeholders.length);
        setPlaceholderVisible(true);
      }, 200);
      return () => clearTimeout(timeout);
    }, 3200);
    return () => clearInterval(interval);
  }, [placeholders.length]);

  useEffect(() => {
    if (autoFocus) inputRef.current?.focus();
  }, [autoFocus]);

  function submitTopHit() {
    const hit = results[activeIdx];
    if (hit) {
      navigate({ to: hit.to as never, params: hit.params as never });
      setQuery("");
    }
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      const hit = results[activeIdx];
      if (hit) {
        e.preventDefault();
        submitTopHit();
      } else if (isLarge && query.trim()) {
        e.preventDefault();
        setEnquiryOpen(true);
      }
    }
  }

  if (isLarge) {
    const isGlass = variant === "glass";
    return (
      <div className="relative w-full">
        <div
          className={
            isGlass
              ? "group relative flex h-12 items-center gap-2 rounded-full border border-white/40 bg-white/10 py-1.5 pl-5 pr-1.5 backdrop-blur-md transition focus-within:border-white/70 focus-within:bg-white/15"
              : "group relative flex items-center gap-3 rounded-full border border-border bg-white py-2 pl-5 pr-2 shadow-[0_20px_60px_-20px_rgb(32_56_112/0.35)] transition focus-within:border-accent/60 focus-within:shadow-[0_25px_70px_-15px_rgb(32_56_112/0.45)] md:pl-7 md:pr-3"
          }
        >
          <Search className={isGlass ? "h-4 w-4 shrink-0 text-white/80" : "h-5 w-5 shrink-0 text-primary/60"} />
          <div className="relative min-w-0 flex-1">
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder=""
              aria-label="Ask about KS Lau & Co"
              className={
                isGlass
                  ? "w-full bg-transparent py-2 text-base text-white placeholder:text-white/70 focus:outline-none"
                  : "w-full bg-transparent py-3 text-lg text-primary placeholder:text-muted-foreground/70 focus:outline-none md:text-lg"
              }
            />
            {!query && (
              <span
                aria-hidden="true"
                className={`pointer-events-none absolute inset-y-0 left-0 flex items-center transition-opacity duration-200 ${
                  isGlass ? "text-base text-white/70" : "text-lg text-muted-foreground/70 md:text-lg"
                } ${placeholderVisible ? "opacity-100" : "opacity-0"}`}
              >
                {placeholders[placeholderIdx]}
              </span>
            )}
          </div>
          {query && (
            <button
              type="button"
              onClick={() => {
                setQuery("");
                inputRef.current?.focus();
              }}
              className={
                isGlass
                  ? "grid h-8 w-8 place-items-center rounded-full text-white/80 hover:bg-white/15"
                  : "grid h-9 w-9 place-items-center rounded-full text-muted-foreground hover:bg-primary/10 hover:text-primary"
              }
              aria-label="Clear"
            >
              <X className="h-4 w-4" />
            </button>
          )}
          <button
            type="button"
            onClick={() => {
              if (results.length > 0) submitTopHit();
              else if (query.trim()) setEnquiryOpen(true);
            }}
            disabled={!query.trim()}
            className={
              isGlass
                ? "grid h-9 w-9 shrink-0 place-items-center rounded-full bg-white/20 text-white transition hover:bg-white/30 disabled:cursor-not-allowed disabled:opacity-40"
                : "grid h-11 w-11 shrink-0 place-items-center rounded-full border border-border text-primary transition hover:border-primary/40 hover:bg-primary/5 disabled:cursor-not-allowed disabled:opacity-40 md:h-12 md:w-12"
            }
            aria-label="Search"
          >
            <ArrowRight className={isGlass ? "h-4 w-4" : "h-5 w-5"} />
          </button>
        </div>

        {/* One-time soft glow pulse */}
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 -z-10 rounded-full bg-accent/20 blur-2xl motion-safe:animate-[heroPulse_1.4s_ease-out_0.4s_1_normal_both]"
        />


        {suggestions && suggestions.length > 0 && !query.trim() && (
          <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              <Sparkles className="h-3 w-3 text-gold" /> Try
            </span>
            {suggestions.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => {
                  setQuery(s);
                  inputRef.current?.focus();
                }}
                className="rounded-full border border-border bg-white/90 px-3.5 py-1.5 text-sm font-medium text-primary backdrop-blur transition hover:border-accent/60 hover:text-gold"
              >
                {s}
              </button>
            ))}
          </div>
        )}

        {query.trim() && (
          <div className={`absolute inset-x-0 z-30 ${resultsPosition === "above" ? "bottom-full mb-3" : "top-full mt-3"}`}>
            {results.length > 0 ? (
              <SearchResults
                results={results}
                activeIdx={activeIdx}
                setActiveIdx={setActiveIdx}
                onSelect={() => setQuery("")}
                query={query}
              />
            ) : (
              <div className="rounded-md border border-border bg-white p-5 text-left shadow-[0_20px_60px_-20px_rgb(32_56_112/0.35)]">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                  No exact match
                </p>
                <p className="mt-2 text-base text-primary">
                  We couldn't find a page for{" "}
                  <span className="font-semibold">"{query}"</span>. Our team can answer this directly.
                </p>
                <button
                  type="button"
                  onClick={() => setEnquiryOpen(true)}
                  className="mt-4 inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2.5 text-base font-semibold text-primary-foreground transition hover:bg-primary/90"
                >
                  <MessageSquarePlus className="h-4 w-4" />
                  Submit as enquiry
                  <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>
        )}

        <EnquiryDialog
          open={enquiryOpen}
          onOpenChange={(o) => {
            setEnquiryOpen(o);
            if (!o) setQuery("");
          }}
          defaultQuestion={query}
        />
      </div>
    );
  }

  return (
    <div className="relative w-full max-w-xl">
      <div className="flex items-center gap-3 rounded-full border border-border bg-white px-5 py-3.5 shadow-[0_10px_40px_-12px_rgb(32_56_112/0.25)] focus-within:border-primary/40 focus-within:ring-2 focus-within:ring-primary/15">
        <Search className="h-4 w-4 shrink-0 text-primary/70" />
        <input
          ref={inputRef}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Search services, insights, people…"
          className="min-w-0 flex-1 bg-transparent text-base text-primary placeholder:text-muted-foreground/70 focus:outline-none"
          aria-label="Search the site"
        />
        {query ? (
          <button
            type="button"
            onClick={() => {
              setQuery("");
              inputRef.current?.focus();
            }}
            className="grid h-6 w-6 place-items-center rounded-full text-muted-foreground hover:bg-primary/10 hover:text-primary"
            aria-label="Clear search"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        ) : (
          <kbd className="hidden items-center rounded-md border border-primary/15 bg-primary/5 px-2 py-0.5 text-[10px] font-semibold text-primary/70 sm:inline-flex">
            ⌘K
          </kbd>
        )}
      </div>
      {query.trim() && (
        <div className="absolute inset-x-0 top-full z-30 mt-2">
          <SearchResults
            results={results}
            activeIdx={activeIdx}
            setActiveIdx={setActiveIdx}
            onSelect={() => setQuery("")}
            query={query}
          />
        </div>
      )}
    </div>
  );
}
